import { questionApplies } from "./rules";
import {
  SCHEMA_VERSION,
  type Answers,
  type DiscoveryContext,
  type Question,
  type QuestionOption,
} from "./types";

const legacyCore = ["legacy-writing-core", "legacy-visual-core", "legacy-brand-core"];
const option = (value: string, label: string, detail?: string): QuestionOption => ({ value, label, detail });

function q(
  question: Omit<Question, "schemaVersion" | "provenance"> & { provenance?: string[] },
): Question {
  return {
    ...question,
    schemaVersion: SCHEMA_VERSION,
    provenance: question.provenance ?? legacyCore,
  };
}

const longGuidance = (recommendedMin: number, recommendedMax: number, note: string) => ({
  unit: "characters" as const,
  recommendedMin,
  recommendedMax,
  hardMaxCharacters: 12_000,
  note,
});

export const questions: Question[] = [
  q({
    id: "project.title",
    chapter: "orientation",
    chapterTitle: "Name the thing",
    chapterNote: "A working title is enough. Nothing here needs to sound finished.",
    label: "What should we call this project?",
    shortLabel: "Project title",
    answerType: "short",
    placeholder: "A working title",
    required: true,
    sensitivity: "public_safe",
    uncertaintyAllowed: false,
    mapsTo: ["project"],
  }),
  q({
    id: "project.stage",
    chapter: "orientation",
    chapterTitle: "Name the thing",
    chapterNote: "Stage changes what the deck can honestly claim.",
    label: "Where is the project right now?",
    shortLabel: "Current stage",
    answerType: "choice",
    options: [
      option("idea", "An idea", "The core exists; much is still open."),
      option("development", "In development", "Material exists and decisions are moving."),
      option("package", "Being packaged", "Team, finance, buyers, or partners are being assembled."),
      option("production", "In production", "The work is being made."),
      option("finished", "Made / launched", "The deck now supports sales, press, partners, or a next chapter."),
      option("unknown", "Not sure yet"),
    ],
    required: true,
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    mapsTo: ["project", "production"],
  }),
  q({
    id: "decision.outcome",
    chapter: "decision",
    chapterTitle: "The decision",
    chapterNote: "A deck is useful when it changes a real decision—not when it merely exists.",
    label: "What should become possible after someone reads this deck?",
    shortLabel: "Desired decision",
    whyAsked: "This becomes the deck’s organising job. Every section must help this happen.",
    answerType: "long",
    placeholder: "Who should decide or do what—and what should happen next?",
    required: true,
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(220, 700, "Name the person, decision, and next action if you can."),
    exampleId: "decision",
    mapsTo: ["decision", "risk"],
  }),
  q({
    id: "audience.primary",
    chapter: "audience",
    chapterTitle: "The reader",
    chapterNote: "Not ‘everyone’. The person whose decision matters first.",
    label: "Who is the primary reader—and what do they already know?",
    shortLabel: "Primary reader",
    answerType: "long",
    placeholder: "Role, context, existing knowledge, and relationship to the project",
    required: true,
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(180, 650, "Specific context helps more than demographics alone."),
    mapsTo: ["audience"],
  }),
  q({
    id: "audience.objections",
    chapter: "audience",
    chapterTitle: "The reader",
    chapterNote: "Respect the reader enough to name what may stop them.",
    label: "What might make this reader say no, hesitate, or misunderstand?",
    shortLabel: "Objections",
    answerType: "long",
    placeholder: "Money, risk, taste, timing, proof, politics, fit, confusion…",
    required: false,
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(120, 650, "List the real resistance, not the answer you wish they had."),
    mapsTo: ["audience", "risk"],
  }),
  q({
    id: "audience.setting",
    chapter: "audience",
    chapterTitle: "The reader",
    chapterNote: "A live pitch and a PDF opened on a phone are different objects.",
    label: "How will the deck probably be experienced?",
    shortLabel: "Reading setting",
    answerType: "multi",
    options: [
      option("emailed", "Read alone after an email"),
      option("live", "Presented live in a room"),
      option("video_call", "Presented on a video call"),
      option("mobile", "Often opened on a phone"),
      option("print", "Printed"),
      option("screen", "Shown on a large screen"),
      option("unknown", "Not known yet"),
    ],
    required: true,
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    mapsTo: ["audience", "visual", "production"],
  }),
  q({
    id: "success.after",
    chapter: "stakes",
    chapterTitle: "What good looks like",
    chapterNote: "The deck is one part of a larger movement.",
    label: "If this works, what happens next in the real world?",
    shortLabel: "Success after reading",
    answerType: "long",
    placeholder: "A meeting, introduction, approval, shortlist, investment, greenlight…",
    required: true,
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(120, 500, "Describe the observable next step, not a quality score for the deck."),
    mapsTo: ["decision", "production"],
  }),
  q({
    id: "core.must_include",
    chapter: "boundaries",
    chapterTitle: "The boundaries",
    chapterNote: "Non-negotiables belong in the room early.",
    label: "What must the deck include or protect?",
    shortLabel: "Must include",
    answerType: "long",
    placeholder: "Facts, people, credit, scenes, terms, claims, images, politics, promises…",
    required: false,
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(80, 700, "Name facts and protected material. Do not design the slide yet."),
    mapsTo: ["story", "proof", "risk"],
  }),
  q({
    id: "core.must_avoid",
    chapter: "boundaries",
    chapterTitle: "The boundaries",
    chapterNote: "A useful ‘no’ can save weeks of polished wrongness.",
    label: "What must this deck never become?",
    shortLabel: "Must avoid",
    answerType: "long",
    placeholder: "A tone, claim, cliché, visual language, audience assumption, or ethical line",
    required: false,
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(80, 600, "Be specific enough that another person could recognise the failure."),
    mapsTo: ["risk", "visual", "story"],
  }),
  q({
    id: "core.materials",
    chapter: "materials",
    chapterTitle: "What exists",
    chapterNote: "We can only build from material that exists—or name what must be created.",
    label: "What do you already have?",
    shortLabel: "Existing material",
    answerType: "multi",
    options: [
      option("notes", "Notes / rough thinking"),
      option("outline", "Outline / structure"),
      option("script", "Script / treatment / manuscript"),
      option("copy", "Draft deck copy"),
      option("deck", "An existing deck"),
      option("brand", "Brand system / guidelines"),
      option("images", "Images / footage / artwork"),
      option("data", "Data / research / evidence"),
      option("none", "Nothing yet"),
    ],
    required: true,
    sensitivity: "public_safe",
    uncertaintyAllowed: false,
    mapsTo: ["project", "proof", "production"],
  }),
  q({
    id: "core.unknowns",
    chapter: "materials",
    chapterTitle: "What exists",
    chapterNote: "Unknown is a valid state. Hidden unknown is where trouble grows.",
    label: "What is still unknown, disputed, or waiting on somebody else?",
    shortLabel: "Open unknowns",
    answerType: "long",
    placeholder: "Questions, contradictions, missing facts, approvals, rights, dates…",
    required: false,
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(80, 800, "List uncertainty plainly. Nobody gets extra marks for pretending."),
    mapsTo: ["risk", "approval", "production"],
  }),

  // Film
  q({
    id: "film.form",
    chapter: "film_shape",
    chapterTitle: "The film",
    chapterNote: "Form changes the evidence, story, and package the deck must carry.",
    label: "What kind of film is it?",
    shortLabel: "Film form",
    answerType: "choice",
    options: [
      option("feature", "Narrative feature"),
      option("short", "Narrative short"),
      option("documentary", "Documentary"),
      option("hybrid", "Hybrid / experimental"),
      option("unknown", "Still deciding"),
    ],
    required: true,
    verticals: ["film"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    mapsTo: ["project", "story"],
    provenance: ["legacy-writing-core", "legacy-film-tv-visual"],
  }),
  q({
    id: "film.logline",
    chapter: "film_story",
    chapterTitle: "The story",
    chapterNote: "One dramatic proposition before the full plot.",
    label: "What is the clearest honest version of the logline today?",
    shortLabel: "Logline",
    answerType: "long",
    placeholder: "When… a person must… before / or else…",
    required: true,
    verticals: ["film"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 250, "One dramatic proposition, not the entire plot. Guidance, not a rule."),
    exampleId: "filmLogline",
    mapsTo: ["story"],
  }),
  q({
    id: "film.synopsis",
    chapter: "film_story",
    chapterTitle: "The story",
    chapterNote: "Setup, escalation, choice, change.",
    label: "Tell the story from beginning to end as it currently stands.",
    shortLabel: "Synopsis",
    answerType: "long",
    placeholder: "What happens, why it matters, and how the central pressure changes the characters",
    required: true,
    verticals: ["film"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(900, 1800, "About 150–300 words often works for deck use. Longer or shorter may be right."),
    exampleId: "synopsis",
    mapsTo: ["story"],
  }),
  q({
    id: "film.protagonist",
    chapter: "film_people",
    chapterTitle: "Who carries it",
    chapterNote: "Desire and contradiction tell us more than biography.",
    label: "Who carries the story—and what do they want, fear, and refuse to admit?",
    shortLabel: "Protagonist",
    answerType: "long",
    required: true,
    verticals: ["film"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(300, 700, "Focus on desire, contradiction, pressure, and change."),
    mapsTo: ["story"],
  }),
  q({
    id: "film.world",
    chapter: "film_people",
    chapterTitle: "Who carries it",
    chapterNote: "The world should create pressure, not merely provide wallpaper.",
    label: "What is specific about the world of this film?",
    shortLabel: "World",
    answerType: "long",
    required: false,
    verticals: ["film"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(180, 750, "Place, rules, labour, texture, community, and what this world makes possible or impossible."),
    mapsTo: ["story", "visual"],
  }),
  q({
    id: "film.themes",
    chapter: "film_meaning",
    chapterTitle: "What remains",
    chapterNote: "Not a list of abstract nouns. The argument living inside the story.",
    label: "Which idea should remain after the plot details fade?",
    shortLabel: "Themes",
    answerType: "long",
    required: false,
    verticals: ["film"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(120, 600, "A tension or question usually carries more life than a slogan."),
    mapsTo: ["story", "visual"],
  }),
  q({
    id: "film.tone",
    chapter: "film_meaning",
    chapterTitle: "What remains",
    chapterNote: "Tone is how the project handles its subject, not a pile of genre labels.",
    label: "How should the film make us feel—and how should it not?",
    shortLabel: "Tone",
    answerType: "long",
    required: false,
    verticals: ["film"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(120, 650, "Name emotional movement, humour, distance, discomfort, or restraint."),
    mapsTo: ["story", "visual"],
  }),
  q({
    id: "film.why_now",
    chapter: "film_authority",
    chapterTitle: "Why this, why you, why now",
    chapterNote: "Authority can come from life, research, access, craft, or commitment. Do not manufacture it.",
    label: "What connects you to this story, and why are you making it now?",
    shortLabel: "Creator note",
    answerType: "long",
    required: false,
    verticals: ["film"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(700, 1600, "Personal connection, authority, and urgency. Guidance, not a demand for confession."),
    exampleId: "creatorNote",
    mapsTo: ["story", "project"],
  }),
  q({
    id: "film.package",
    chapter: "film_authority",
    chapterTitle: "Why this, why you, why now",
    chapterNote: "Name what is confirmed. Keep aspirations labelled as aspirations.",
    label: "What is genuinely attached, confirmed, or achieved so far?",
    shortLabel: "Package and progress",
    answerType: "long",
    placeholder: "Team, cast, producers, finance, labs, awards, access, partners—plus what remains unconfirmed",
    required: false,
    verticals: ["film"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 800, "Separate confirmed, in conversation, and hoped for."),
    mapsTo: ["proof", "production", "risk"],
  }),
  q({
    id: "film.documentary_access",
    chapter: "documentary_truth",
    chapterTitle: "Access and responsibility",
    chapterNote: "Documentary access is not the same as permission, safety, or truth.",
    label: "What access do you have—and what access is only hoped for?",
    shortLabel: "Documentary access",
    answerType: "long",
    required: true,
    verticals: ["film"],
    visibleWhen: { field: "film.form", equals: "documentary" },
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    mapsTo: ["proof", "risk", "production"],
    provenance: ["actual-goal-documentary-gap"],
  }),
  q({
    id: "film.documentary_consent",
    chapter: "documentary_truth",
    chapterTitle: "Access and responsibility",
    chapterNote: "The deck must not turn someone else’s risk into our proof.",
    label: "Whose consent, safety, anonymity, or dignity must shape the project?",
    shortLabel: "Consent and safety",
    answerType: "long",
    required: true,
    verticals: ["film"],
    visibleWhen: { field: "film.form", equals: "documentary" },
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    mapsTo: ["risk", "approval"],
    provenance: ["actual-goal-documentary-gap"],
  }),
  q({
    id: "film.documentary_archive",
    chapter: "documentary_truth",
    chapterTitle: "Access and responsibility",
    chapterNote: "Availability and permission are different facts.",
    label: "What archive, footage, likeness, music, or reporting rights are confirmed or unclear?",
    shortLabel: "Archive and rights",
    answerType: "long",
    required: false,
    verticals: ["film"],
    visibleWhen: { field: "film.form", equals: "documentary" },
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    mapsTo: ["proof", "risk", "production"],
    provenance: ["actual-goal-documentary-gap"],
  }),

  // TV / series
  q({
    id: "tv.form",
    chapter: "tv_shape",
    chapterTitle: "The series",
    chapterNote: "Series form determines what must repeat and what must resolve.",
    label: "What kind of series is it?",
    shortLabel: "Series form",
    answerType: "choice",
    options: [
      option("scripted_limited", "Scripted limited series"),
      option("scripted_returning", "Scripted returning series"),
      option("unscripted_limited", "Unscripted limited series"),
      option("unscripted_returning", "Unscripted returning format"),
      option("anthology", "Anthology"),
      option("unknown", "Still deciding"),
    ],
    required: true,
    verticals: ["tv"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    mapsTo: ["project", "story"],
  }),
  q({
    id: "tv.logline",
    chapter: "tv_premise",
    chapterTitle: "Premise and engine",
    chapterNote: "Premise opens the door. Engine explains why it stays open.",
    label: "What is the series in one honest, vivid proposition?",
    shortLabel: "Series logline",
    answerType: "long",
    required: true,
    verticals: ["tv"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 300, "Introduce the central world, pressure, and promise."),
    mapsTo: ["story"],
  }),
  q({
    id: "tv.engine",
    chapter: "tv_premise",
    chapterTitle: "Premise and engine",
    chapterNote: "What produces another episode when episode one is finished?",
    label: "What is the repeatable series engine?",
    shortLabel: "Series engine",
    answerType: "long",
    required: true,
    verticals: ["tv"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(300, 900, "Name the recurring source of story, pressure, and variation."),
    exampleId: "seriesEngine",
    mapsTo: ["story"],
  }),
  q({
    id: "tv.overview",
    chapter: "tv_world",
    chapterTitle: "World and people",
    chapterNote: "The series overview holds the whole promise—not a longer logline.",
    label: "Describe the series world, central ensemble, and ongoing tension.",
    shortLabel: "Series overview",
    answerType: "long",
    required: true,
    verticals: ["tv"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(700, 1500, "About 120–250 words often works. Keep the engine and season arc separate."),
    mapsTo: ["story"],
  }),
  q({
    id: "tv.pilot",
    chapter: "tv_world",
    chapterTitle: "World and people",
    chapterNote: "The pilot must start the series, not merely introduce it.",
    label: "What changes in the pilot—and what question does it leave alive?",
    shortLabel: "Pilot movement",
    answerType: "long",
    required: false,
    verticals: ["tv"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(300, 1000, "Describe the irreversible movement that launches the series."),
    mapsTo: ["story"],
  }),
  q({
    id: "tv.season_arc",
    chapter: "tv_arc",
    chapterTitle: "Season movement",
    chapterNote: "A season needs cumulative change, even when episodes stand alone.",
    label: "How does the first season gather, turn, and land?",
    shortLabel: "Season arc",
    answerType: "long",
    required: true,
    verticals: ["tv"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(500, 1400, "Beginning state, escalation, major turn, ending state."),
    mapsTo: ["story"],
  }),
  q({
    id: "tv.episode_shape",
    chapter: "tv_arc",
    chapterTitle: "Season movement",
    chapterNote: "Pattern gives confidence; variation gives life.",
    label: "What tends to happen inside an episode—and what can change?",
    shortLabel: "Episode shape",
    answerType: "long",
    required: false,
    verticals: ["tv"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(220, 850, "Include duration, structure, recurring beats, or format rules where useful."),
    mapsTo: ["story", "production"],
  }),
  q({
    id: "tv.future",
    chapter: "tv_future",
    chapterTitle: "Life beyond season one",
    chapterNote: "Potential is more credible when it has a mechanism.",
    label: "If the series returns, where does new story come from?",
    shortLabel: "Future seasons",
    answerType: "long",
    required: false,
    verticals: ["tv"],
    visibleWhen: { any: [{ field: "tv.form", equals: "scripted_returning" }, { field: "tv.form", equals: "unscripted_returning" }, { field: "tv.form", equals: "anthology" }] },
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(180, 700, "Name durable sources of renewal, not ‘more adventures’."),
    mapsTo: ["story", "production"],
  }),
  q({
    id: "tv.package",
    chapter: "tv_future",
    chapterTitle: "Life beyond season one",
    chapterNote: "Keep confirmed attachments separate from ambition.",
    label: "What material, talent, access, partners, or development progress is confirmed?",
    shortLabel: "Package and progress",
    answerType: "long",
    required: false,
    verticals: ["tv"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 800, "Separate confirmed, in conversation, and hoped for."),
    mapsTo: ["proof", "production", "risk"],
  }),

  // Advertising
  q({
    id: "ads.pitch_type",
    chapter: "ads_brief",
    chapterTitle: "The brief",
    chapterNote: "A director treatment, agency response, and production pitch answer different questions.",
    label: "What kind of advertising deck is this?",
    shortLabel: "Pitch type",
    answerType: "choice",
    options: [
      option("director_treatment", "Director treatment"),
      option("agency_response", "Agency creative response"),
      option("production_pitch", "Production company pitch"),
      option("integrated_campaign", "Integrated campaign deck"),
      option("other", "Something else"),
    ],
    required: true,
    verticals: ["advertising"],
    sensitivity: "public_safe",
    uncertaintyAllowed: false,
    mapsTo: ["project", "production"],
  }),
  q({
    id: "ads.brand_product",
    chapter: "ads_brief",
    chapterTitle: "The brief",
    chapterNote: "The product and brand are story conditions, not late-stage inserts.",
    label: "What brand, product, service, or behaviour sits at the centre?",
    shortLabel: "Brand / product",
    answerType: "long",
    required: true,
    verticals: ["advertising"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 500, "Describe what it does, what people believe now, and any useful tension."),
    mapsTo: ["project", "story"],
  }),
  q({
    id: "ads.objective",
    chapter: "ads_strategy",
    chapterTitle: "The communication",
    chapterNote: "One communication job. Other benefits can follow.",
    label: "What should the work make the audience think, feel, or do?",
    shortLabel: "Communication objective",
    answerType: "long",
    required: true,
    verticals: ["advertising"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(120, 550, "Prioritise the one change the work must earn."),
    mapsTo: ["decision", "audience"],
  }),
  q({
    id: "ads.proposition",
    chapter: "ads_strategy",
    chapterTitle: "The communication",
    chapterNote: "The proposition is the useful truth the idea must carry.",
    label: "What single-minded proposition or product truth should survive?",
    shortLabel: "Proposition",
    answerType: "long",
    required: false,
    verticals: ["advertising"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(60, 350, "Plain language first. A slogan can come later."),
    mapsTo: ["story", "proof"],
  }),
  q({
    id: "ads.idea",
    chapter: "ads_idea",
    chapterTitle: "The idea",
    chapterNote: "Make the film legible before making it impressive.",
    label: "What happens—and where does the emotional movement come from?",
    shortLabel: "Film / campaign idea",
    answerType: "long",
    required: true,
    verticals: ["advertising"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(500, 1200, "Idea, emotional movement, and product role. Camera coverage can wait."),
    exampleId: "adIdea",
    mapsTo: ["story", "visual"],
  }),
  q({
    id: "ads.product_role",
    chapter: "ads_idea",
    chapterTitle: "The idea",
    chapterNote: "If the product can vanish without changing the idea, the integration is not done.",
    label: "What necessary role does the product or brand play in the story?",
    shortLabel: "Product role",
    answerType: "long",
    required: true,
    verticals: ["advertising"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 550, "Cause, tool, reward, evidence, world rule—or another honest role."),
    mapsTo: ["story", "proof"],
  }),
  q({
    id: "ads.mandatories",
    chapter: "ads_constraints",
    chapterTitle: "The non-negotiables",
    chapterNote: "Make the box visible. Then make good work inside it.",
    label: "What mandatories, claims, pack shots, lines, people, or brand rules are fixed?",
    shortLabel: "Mandatories",
    answerType: "long",
    required: false,
    verticals: ["advertising"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(80, 800, "Separate fixed requirements from preferences or inherited habits."),
    mapsTo: ["risk", "production", "approval"],
  }),
  q({
    id: "ads.deliverables",
    chapter: "ads_constraints",
    chapterTitle: "The non-negotiables",
    chapterNote: "The deck should anticipate the work’s real shapes.",
    label: "What durations, channels, ratios, cutdowns, markets, or accessibility versions are expected?",
    shortLabel: "Deliverables",
    answerType: "long",
    required: false,
    verticals: ["advertising"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(80, 700, "Include unknowns. Do not invent a production scope to make the answer look complete."),
    mapsTo: ["production", "visual", "risk"],
  }),
  q({
    id: "ads.production",
    chapter: "ads_making",
    chapterTitle: "How it can be made",
    chapterNote: "A treatment should make ambition and feasibility speak to each other.",
    label: "Which production conditions materially shape the idea?",
    shortLabel: "Production conditions",
    answerType: "long",
    placeholder: "Budget band, schedule, locations, casting, VFX, weather, legal, usage…",
    required: false,
    verticals: ["advertising"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(120, 800, "Name constraints only when known. Mark assumptions."),
    mapsTo: ["production", "risk"],
  }),

  // Business
  q({
    id: "business.deck_type",
    chapter: "business_shape",
    chapterTitle: "The business decision",
    chapterNote: "Different decisions need different proof.",
    label: "What kind of business deck is this?",
    shortLabel: "Deck type",
    answerType: "choice",
    options: [
      option("fundraising", "Fundraising"),
      option("sales", "Sales / new business"),
      option("partnership", "Partnership"),
      option("credentials", "Credentials / company profile"),
      option("internal", "Internal decision"),
      option("other", "Something else"),
    ],
    required: true,
    verticals: ["business"],
    sensitivity: "public_safe",
    uncertaintyAllowed: false,
    mapsTo: ["project", "decision"],
    provenance: ["legacy-company-profile", "legacy-brand-core"],
  }),
  q({
    id: "business.customer",
    chapter: "business_customer",
    chapterTitle: "The person with the problem",
    chapterNote: "Specific people and work, not a market-shaped cloud.",
    label: "Who has the problem, and what are they trying to get done?",
    shortLabel: "Customer",
    answerType: "long",
    required: true,
    verticals: ["business"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(180, 700, "Role, situation, current workaround, and cost of the problem."),
    mapsTo: ["audience", "story"],
  }),
  q({
    id: "business.problem",
    chapter: "business_customer",
    chapterTitle: "The person with the problem",
    chapterNote: "A strong problem is observable and costly, not merely large.",
    label: "What is broken, expensive, slow, risky, or impossible today?",
    shortLabel: "Problem",
    answerType: "long",
    required: true,
    verticals: ["business"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(350, 900, "Describe the workflow and consequence. Guidance, not a word quota."),
    exampleId: "businessProblem",
    mapsTo: ["story", "proof"],
  }),
  q({
    id: "business.solution",
    chapter: "business_answer",
    chapterTitle: "The answer",
    chapterNote: "Mechanism before adjectives.",
    label: "How does your product, service, or company change that situation?",
    shortLabel: "Solution",
    answerType: "long",
    required: true,
    verticals: ["business"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(350, 900, "Explain what happens differently and why that produces value."),
    mapsTo: ["story", "proof"],
  }),
  q({
    id: "business.proof",
    chapter: "business_proof",
    chapterTitle: "The proof",
    chapterNote: "Claims without sources create future correction work.",
    label: "Which evidence could change the reader’s decision—and where does it come from?",
    shortLabel: "Evidence",
    answerType: "long",
    required: true,
    verticals: ["business"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(180, 1000, "Pair each important claim with a source and confidence boundary."),
    exampleId: "evidence",
    mapsTo: ["proof", "risk"],
  }),
  q({
    id: "business.model",
    chapter: "business_proof",
    chapterTitle: "The proof",
    chapterNote: "Say how value and money move. Skip theatre.",
    label: "How does the work create value, earn money, or sustain itself?",
    shortLabel: "Model",
    answerType: "long",
    required: false,
    verticals: ["business"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(120, 700, "Who pays, for what, how often, and what matters economically."),
    mapsTo: ["story", "proof"],
  }),
  q({
    id: "business.market",
    chapter: "business_position",
    chapterTitle: "The field around you",
    chapterNote: "A market number without a route to it proves little.",
    label: "What is the relevant market or opportunity—and what supports that view?",
    shortLabel: "Market",
    answerType: "long",
    required: false,
    verticals: ["business"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(150, 900, "Use sources and assumptions. ‘Huge market’ is not evidence."),
    mapsTo: ["proof", "risk"],
  }),
  q({
    id: "business.difference",
    chapter: "business_position",
    chapterTitle: "The field around you",
    chapterNote: "Difference matters when it changes the customer’s choice.",
    label: "Why would the right person choose this over doing nothing or using an alternative?",
    shortLabel: "Difference",
    answerType: "long",
    required: true,
    verticals: ["business"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(150, 750, "Compare mechanisms, trade-offs, access, trust, or economics—not adjectives."),
    mapsTo: ["story", "proof"],
  }),
  q({
    id: "business.ask",
    chapter: "business_ask",
    chapterTitle: "The ask",
    chapterNote: "The last slide should not reveal that nobody decided what to ask for.",
    label: "What exactly are you asking the reader to do, commit, or approve?",
    shortLabel: "Ask",
    answerType: "long",
    required: true,
    verticals: ["business"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(80, 500, "Amount, action, introduction, meeting, approval, or next step—when known."),
    mapsTo: ["decision", "production"],
  }),
  q({
    id: "business.team",
    chapter: "business_ask",
    chapterTitle: "The ask",
    chapterNote: "Team proof is about why this group can do this work.",
    label: "Whose experience, access, or labour makes the plan credible?",
    shortLabel: "Team authority",
    answerType: "long",
    required: false,
    verticals: ["business"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(120, 850, "Use relevant evidence. Preserve accurate roles and credit."),
    mapsTo: ["proof", "project"],
  }),

  // General / other
  q({
    id: "general.kind",
    chapter: "general_shape",
    chapterTitle: "Find the nearest shape",
    chapterNote: "‘Other’ should still receive a useful route.",
    label: "What are you trying to make or explain?",
    shortLabel: "Project kind",
    answerType: "long",
    required: true,
    verticals: ["general"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(120, 650, "Describe the thing and the context in which it must work."),
    mapsTo: ["project", "story"],
  }),
  q({
    id: "general.change",
    chapter: "general_shape",
    chapterTitle: "Find the nearest shape",
    chapterNote: "Every deck carries a change: belief, permission, money, action, or memory.",
    label: "What should change for the reader?",
    shortLabel: "Reader change",
    answerType: "long",
    required: true,
    verticals: ["general"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(120, 550, "Name what they understand, believe, decide, or do differently."),
    mapsTo: ["decision", "audience"],
  }),
  q({
    id: "general.material",
    chapter: "general_content",
    chapterTitle: "The material",
    chapterNote: "Give us the spine before the slide list.",
    label: "What are the essential ideas, events, claims, or proof the deck must carry?",
    shortLabel: "Essential material",
    answerType: "long",
    required: true,
    verticals: ["general"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(250, 1200, "Use a rough list if that is more honest than polished prose."),
    mapsTo: ["story", "proof"],
  }),
  q({
    id: "general.nearest",
    chapter: "general_content",
    chapterTitle: "The material",
    chapterNote: "Borrow the nearest useful logic without pretending the project is something else.",
    label: "Which familiar deck shape is closest—and where does it stop fitting?",
    shortLabel: "Nearest deck shape",
    answerType: "choice",
    options: [
      option("story", "A story / screen project"),
      option("campaign", "A campaign / creative treatment"),
      option("business", "A business / organisation"),
      option("cultural", "A cultural / community project"),
      option("none", "None of these"),
    ],
    required: false,
    verticals: ["general"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    mapsTo: ["story", "risk"],
  }),

  // Writing service
  q({
    id: "writing.copy_state",
    chapter: "writing_truth",
    chapterTitle: "The words today",
    chapterNote: "We need to know whether we are discovering, shaping, or refining.",
    label: "What state is the written material in?",
    shortLabel: "Copy state",
    answerType: "choice",
    options: [
      option("absent", "Mostly absent"),
      option("rough", "Rough notes / fragments"),
      option("structured", "Structured draft"),
      option("reviewed", "Reviewed copy"),
      option("locked", "Locked copy"),
      option("mixed", "Different sections are at different stages"),
    ],
    required: true,
    services: ["writing", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: false,
    mapsTo: ["story", "production"],
  }),
  q({
    id: "writing.source_order",
    chapter: "writing_truth",
    chapterTitle: "The words today",
    chapterNote: "When sources disagree, the writer needs an authority order.",
    label: "Which materials are authoritative—and what should happen when they conflict?",
    shortLabel: "Source order",
    answerType: "long",
    required: false,
    services: ["writing", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(120, 750, "For example: current script first, then director notes, then old deck only for verified facts."),
    mapsTo: ["proof", "approval", "risk"],
  }),
  q({
    id: "writing.voice",
    chapter: "writing_voice",
    chapterTitle: "Who is speaking",
    chapterNote: "Voice is a relationship to the material, not a bag of adjectives.",
    label: "Who should the deck sound like—and what relationship should it have with the reader?",
    shortLabel: "Voice",
    answerType: "long",
    required: true,
    services: ["writing", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(150, 700, "Name speaker, point of view, knowledge, warmth, distance, humour, or restraint."),
    mapsTo: ["story", "audience"],
  }),
  q({
    id: "writing.forbidden",
    chapter: "writing_voice",
    chapterTitle: "Who is speaking",
    chapterNote: "Protect vocabulary, truth, and the project’s dignity.",
    label: "Which words, claims, tones, or habits must the writing avoid?",
    shortLabel: "Forbidden language",
    answerType: "long",
    required: false,
    services: ["writing", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(80, 600, "Include unsupported claims and language that would make the project stop sounding like itself."),
    mapsTo: ["risk", "story"],
  }),
  q({
    id: "writing.claims",
    chapter: "writing_evidence",
    chapterTitle: "Claims and evidence",
    chapterNote: "Known, believed, hoped, and unknown must not collapse into one confident sentence.",
    label: "Which important claims need proof, qualification, or permission?",
    shortLabel: "Claims to verify",
    answerType: "long",
    required: false,
    services: ["writing", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 900, "Pair claims with source links or name them as unresolved."),
    exampleId: "evidence",
    mapsTo: ["proof", "risk"],
  }),
  q({
    id: "writing.approval",
    chapter: "writing_evidence",
    chapterTitle: "Claims and evidence",
    chapterNote: "Feedback is easier when one person can resolve a conflict.",
    label: "Who decides when writing feedback conflicts?",
    shortLabel: "Writing decision owner",
    answerType: "long",
    required: false,
    services: ["writing", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(60, 450, "A role is enough. Name what they can approve."),
    mapsTo: ["approval", "production"],
  }),

  // Design service
  q({
    id: "design.copy_state",
    chapter: "design_truth",
    chapterTitle: "The design conditions",
    chapterNote: "Design can shape copy, but it cannot pretend unfinished copy is locked.",
    label: "How stable is the copy that design must work with?",
    shortLabel: "Copy stability",
    answerType: "choice",
    options: [
      option("absent", "Mostly absent"),
      option("rough", "Rough / changing"),
      option("structured", "Structured but editable"),
      option("reviewed", "Reviewed; small changes likely"),
      option("locked", "Locked"),
      option("mixed", "Mixed by section"),
    ],
    required: true,
    services: ["design", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: false,
    mapsTo: ["visual", "production", "risk"],
  }),
  q({
    id: "design.brand_system",
    chapter: "design_truth",
    chapterTitle: "The design conditions",
    chapterNote: "Existing rules can be law, evidence, or a starting conversation.",
    label: "What brand system, assets, or visual rules already exist—and how binding are they?",
    shortLabel: "Brand authority",
    answerType: "long",
    required: false,
    services: ["design", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 700, "Separate non-negotiables, useful assets, preferences, and things open to challenge."),
    mapsTo: ["visual", "risk"],
  }),
  q({
    id: "design.feeling",
    chapter: "design_direction",
    chapterTitle: "The visual job",
    chapterNote: "Feeling matters when it changes how the work is read.",
    label: "What should the visual language make the reader feel or understand?",
    shortLabel: "Visual effect",
    answerType: "long",
    required: true,
    services: ["design", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(150, 700, "Connect feeling to the project’s argument. ‘Premium’ alone tells us very little."),
    mapsTo: ["visual", "audience"],
  }),
  q({
    id: "design.references",
    chapter: "design_direction",
    chapterTitle: "The visual job",
    chapterNote: "A reference is useful when its mechanism is named.",
    label: "Which references help—and what exactly should transfer or stay behind?",
    shortLabel: "References",
    answerType: "long",
    required: false,
    services: ["design", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(180, 1000, "Up to three references is plenty. Explain each one."),
    exampleId: "references",
    mapsTo: ["visual", "risk"],
  }),
  q({
    id: "design.anti_references",
    chapter: "design_direction",
    chapterTitle: "The visual job",
    chapterNote: "Dislike can be useful evidence when the reason is clear.",
    label: "Which visual directions should we avoid—and why?",
    shortLabel: "Anti-references",
    answerType: "long",
    required: false,
    services: ["design", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 700, "Name the mechanism that fails, not merely the example."),
    mapsTo: ["visual", "risk"],
  }),
  q({
    id: "design.freedom",
    chapter: "design_boundaries",
    chapterTitle: "Freedom and responsibility",
    chapterNote: "A preference and a mandate should not look identical in the brief.",
    label: "Where should design follow, interpret, or challenge the current direction?",
    shortLabel: "Creative freedom",
    answerType: "long",
    required: false,
    services: ["design", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 650, "Name locked decisions, strong preferences, and genuine open territory."),
    mapsTo: ["visual", "approval"],
  }),
  q({
    id: "design.rights",
    chapter: "design_boundaries",
    chapterTitle: "Freedom and responsibility",
    chapterNote: "Finding an image online does not make it available for use.",
    label: "Which image, artwork, archive, likeness, font, or music rights are confirmed or unclear?",
    shortLabel: "Rights",
    answerType: "long",
    required: false,
    services: ["design", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(80, 750, "List owned, licensed, permission-cleared, reference-only, and unknown material separately."),
    mapsTo: ["visual", "risk", "production"],
  }),
  q({
    id: "design.formats",
    chapter: "design_delivery",
    chapterTitle: "Where it must survive",
    chapterNote: "The visual system should be designed for its real rooms.",
    label: "Which screens, ratios, print formats, editable files, or motion versions matter?",
    shortLabel: "Delivery formats",
    answerType: "long",
    required: true,
    services: ["design", "writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 700, "Include accessibility, phone reading, projection, print, and future edits where relevant."),
    mapsTo: ["visual", "production"],
  }),

  // Writing + design integration
  q({
    id: "integration.responsibility",
    chapter: "integration",
    chapterTitle: "Words and pictures together",
    chapterNote: "Shared responsibility needs visible edges.",
    label: "Who supplies, changes, checks, and approves words and visuals?",
    shortLabel: "Responsibility map",
    answerType: "long",
    required: true,
    services: ["writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(140, 750, "Name roles. Include client, studio, legal, producers, and other reviewers where relevant."),
    mapsTo: ["approval", "production"],
  }),
  q({
    id: "integration.copy_lock",
    chapter: "integration",
    chapterTitle: "Words and pictures together",
    chapterNote: "Design discoveries can improve writing. Endless unlocked copy can break design.",
    label: "When is copy stable enough to design—and what changes remain allowed after that?",
    shortLabel: "Copy-lock rule",
    answerType: "long",
    required: false,
    services: ["writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 650, "Define threshold, not a fantasy of perfect final copy."),
    mapsTo: ["approval", "production", "risk"],
  }),
  q({
    id: "integration.conflict",
    chapter: "integration",
    chapterTitle: "Words and pictures together",
    chapterNote: "When words and visuals disagree, somebody must protect the actual deck job.",
    label: "Who resolves a conflict between narrative clarity, visual direction, and stakeholder preference?",
    shortLabel: "Conflict owner",
    answerType: "long",
    required: false,
    services: ["writing_design"],
    sensitivity: "public_safe",
    uncertaintyAllowed: true,
    guidance: longGuidance(80, 500, "Name one decision owner or an exact resolution rule."),
    mapsTo: ["approval", "risk"],
  }),

  // Private client operations
  q({
    id: "private.source_links",
    chapter: "private_sources",
    chapterTitle: "Source room",
    chapterNote: "Private mode can hold the materials the public tool should never demand.",
    label: "Where do the authoritative source materials live?",
    shortLabel: "Source links",
    answerType: "long",
    placeholder: "Drive folders, scripts, briefs, research, brand files, prior decks",
    required: false,
    modes: ["private"],
    sensitivity: "confidential",
    uncertaintyAllowed: true,
    guidance: longGuidance(40, 1200, "Paste links and name what each one governs. Do not paste passwords or private access tokens."),
    mapsTo: ["proof", "production"],
  }),
  q({
    id: "private.stakeholders",
    chapter: "private_people",
    chapterTitle: "People and decisions",
    chapterNote: "Clear approval protects everybody’s time.",
    label: "Who gives input, who approves, and who can stop the work?",
    shortLabel: "Stakeholders",
    answerType: "long",
    required: true,
    modes: ["private"],
    sensitivity: "personal",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 750, "Use names or roles. Distinguish input from final approval."),
    mapsTo: ["approval", "production", "risk"],
  }),
  q({
    id: "private.deadline",
    chapter: "private_timing",
    chapterTitle: "Time and conditions",
    chapterNote: "A date means more when we know what creates it.",
    label: "What is the real deadline?",
    shortLabel: "Deadline",
    answerType: "date",
    required: false,
    modes: ["private"],
    sensitivity: "personal",
    uncertaintyAllowed: true,
    mapsTo: ["production", "risk"],
  }),
  q({
    id: "private.deadline_reason",
    chapter: "private_timing",
    chapterTitle: "Time and conditions",
    chapterNote: "A festival, meeting, shoot, or internal preference creates different risk.",
    label: "What happens on that date—and what can move?",
    shortLabel: "Deadline reason",
    answerType: "long",
    required: false,
    modes: ["private"],
    sensitivity: "personal",
    uncertaintyAllowed: true,
    guidance: longGuidance(60, 500, "Name the external event, dependency, buffer, and flexibility."),
    mapsTo: ["production", "risk"],
  }),
  q({
    id: "private.budget",
    chapter: "private_timing",
    chapterTitle: "Time and conditions",
    chapterNote: "Money shapes scope. It does not measure a project’s worth.",
    label: "What budget or scope boundary should the work respect?",
    shortLabel: "Budget / scope",
    answerType: "long",
    required: false,
    modes: ["private"],
    sensitivity: "confidential",
    uncertaintyAllowed: true,
    guidance: longGuidance(40, 450, "A range, cap, approved scope, or ‘not decided’ is useful."),
    mapsTo: ["production", "risk"],
  }),
  q({
    id: "private.confidentiality",
    chapter: "private_care",
    chapterTitle: "Care and confidentiality",
    chapterNote: "Private material needs explicit boundaries, not vague discretion.",
    label: "What may be shared, shown, credited, anonymised, or never disclosed?",
    shortLabel: "Confidentiality",
    answerType: "long",
    required: false,
    modes: ["private"],
    sensitivity: "confidential",
    uncertaintyAllowed: true,
    guidance: longGuidance(100, 800, "Name people, territories, materials, embargoes, and portfolio permissions where known."),
    mapsTo: ["risk", "approval", "production"],
  }),
  q({
    id: "private.missed",
    chapter: "private_care",
    chapterTitle: "Care and confidentiality",
    chapterNote: "Forms are maps. Real projects can exceed them.",
    label: "What have we failed to ask that could change the work?",
    shortLabel: "What we missed",
    answerType: "long",
    required: false,
    modes: ["private"],
    sensitivity: "confidential",
    uncertaintyAllowed: true,
    guidance: longGuidance(40, 1000, "Anything practical, political, personal, strange, or hard to categorise belongs here."),
    mapsTo: ["risk", "project", "production"],
  }),
];

export function getVisibleQuestions(context: DiscoveryContext, answers: Answers): Question[] {
  return questions.filter((question) => questionApplies(question, context, answers));
}

export function getRouteEstimate(visibleQuestions: Question[]): number {
  const long = visibleQuestions.filter((question) => question.answerType === "long").length;
  const other = visibleQuestions.length - long;
  return Math.max(6, Math.round(long * 1.25 + other * 0.35));
}

export function validateQuestionBank(): string[] {
  const issues: string[] = [];
  const seen = new Set<string>();
  for (const question of questions) {
    if (seen.has(question.id)) issues.push(`Duplicate question id: ${question.id}`);
    seen.add(question.id);
    if (question.schemaVersion !== SCHEMA_VERSION) issues.push(`Wrong schema version: ${question.id}`);
    if (question.sensitivity !== "public_safe" && !question.modes?.includes("private")) {
      issues.push(`Sensitive question is not private-only: ${question.id}`);
    }
    if ((question.answerType === "choice" || question.answerType === "multi") && !question.options?.length) {
      issues.push(`Choice question has no options: ${question.id}`);
    }
    if (!question.mapsTo.length) issues.push(`Question maps nowhere: ${question.id}`);
    if (!question.provenance.length) issues.push(`Question has no provenance: ${question.id}`);
  }
  return issues;
}
